import {
	forwardRef,
	useCallback,
	useImperativeHandle,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import {
	ACTIONS,
	EVENTS,
	STATUS,
	type EventHandler,
	type Step,
} from 'react-joyride'
import CustomJoyride from '../../shared/Joyride/CustomJoyride'

export const DR_SECDIM_TUTORIAL_STORAGE_PREFIX = 'drSecDimTutorialSeen:v1:'

export const drSecDimTutorialStorageKey = (guid: string): string =>
	`${DR_SECDIM_TUTORIAL_STORAGE_PREFIX}${guid}`

export const hasSeenDrSecDimTutorial = (guid: string): boolean => {
	try {
		return localStorage.getItem(drSecDimTutorialStorageKey(guid)) !== null
	} catch {
		return false
	}
}

const markDrSecDimTutorialSeen = (guid: string): void => {
	try {
		localStorage.setItem(drSecDimTutorialStorageKey(guid), 'true')
	} catch {
		return
	}
}

export interface DrSecDimTutorialAvailability {
	hasQuestionnaire: boolean
	hasPath: boolean
	hasRecommendation: boolean
	hasRegenerate: boolean
}

type TutorialStepId =
	| 'trigger'
	| 'panel'
	| 'setup'
	| 'path'
	| 'recommendation'
	| 'regenerate'
	| 'close'

interface TutorialStep extends Step {
	id: TutorialStepId
	panelOpen: boolean
	isAvailable: (availability: DrSecDimTutorialAvailability) => boolean
}

const ALWAYS = () => true

const TUTORIAL_STEPS: TutorialStep[] = [
	{
		id: 'trigger',
		target: '[data-tour="dr-secdim-trigger"]',
		title: 'Meet Dr. SecDim',
		content:
			'This is your personal learning assistant. Open it any time to get a security learning path tailored to you.',
		placement: 'bottom',
		panelOpen: false,
		isAvailable: ALWAYS,
	},
	{
		id: 'panel',
		target:
			'[data-tour="dr-secdim-questionnaire"], [data-tour="dr-secdim-panel-content"]',
		title: 'Your learning workspace',
		content:
			'Dr. SecDim uses your goals and preferences to guide what you learn next.',
		placement: 'bottom',
		panelOpen: true,
		isAvailable: ALWAYS,
	},
	{
		id: 'setup',
		target: '[data-tour="dr-secdim-language-option"]',
		title: 'Tell us about you',
		content:
			"Start here — pick the languages you work with (like Python), then walk through your experience, role, goals, available time and learning style.",
		placement: 'bottom',
		panelOpen: true,
		isAvailable: a => a.hasQuestionnaire,
	},
	{
		id: 'path',
		target: '[data-tour="dr-secdim-path-preview"]',
		title: 'Your learning path',
		content:
			'Once generated, your path lays out an ordered sequence of recommended topics to work through.',
		placement: 'bottom',
		panelOpen: true,
		isAvailable: a => a.hasPath,
	},
	{
		id: 'recommendation',
		target: '[data-tour="dr-secdim-recommendation"]',
		title: 'What to do next',
		content:
			'Your current recommendation highlights the single best thing to focus on right now.',
		placement: 'top',
		panelOpen: true,
		isAvailable: a => a.hasRecommendation,
	},
	{
		id: 'regenerate',
		target: '[data-tour="dr-secdim-regenerate"]',
		title: 'Keep it up to date',
		content:
			'As your goals change, update your preferences or regenerate your path to keep it relevant.',
		placement: 'bottom',
		panelOpen: true,
		isAvailable: a => a.hasRegenerate,
	},
	{
		id: 'close',
		target: '[data-tour="dr-secdim-close"]',
		title: 'Come back any time',
		content:
			'Close the panel whenever you like — your progress is saved, and you can reopen Dr. SecDim from here.',
		placement: 'bottom',
		panelOpen: true,
		isAvailable: ALWAYS,
	},
]

const buildActiveSteps = (
	availability: DrSecDimTutorialAvailability,
): TutorialStep[] =>
	TUTORIAL_STEPS.filter(step => step.isAvailable(availability))

const toJoyrideStep = (step: TutorialStep): Step => {
	const { id, panelOpen, isAvailable, ...joyrideStep } = step
	void id
	void panelOpen
	void isAvailable
	return joyrideStep
}

function waitForPanelTransition(onSettled: () => void): () => void {
	const panelEl = document.querySelector('[data-tour="dr-secdim-panel"]')
	if (!panelEl) {
		onSettled()
		return () => {}
	}
	let settled = false
	const finish = () => {
		if (settled) return
		settled = true
		onSettled()
	}
	const handleTransitionEnd = (event: Event) => {
		if (
			event.target !== panelEl ||
			(event as TransitionEvent).propertyName !== 'transform'
		)
			return
		finish()
	}
	panelEl.addEventListener('transitionend', handleTransitionEnd)
	const fallback = window.setTimeout(finish, 350)
	return () => {
		panelEl.removeEventListener('transitionend', handleTransitionEnd)
		window.clearTimeout(fallback)
	}
}

export interface DrSecDimTutorialHandle {
	restart: () => void
}

interface DrSecDimTutorialProps {
	authenticated: boolean
	profileGuid: string
	isReady: boolean
	isPanelOpen: boolean
	availability: DrSecDimTutorialAvailability
	hasCustomisation: boolean
	onSetPanelOpen: (open: boolean) => void
	onActiveChange?: (active: boolean) => void
}

const DrSecDimTutorial = forwardRef<
	DrSecDimTutorialHandle,
	DrSecDimTutorialProps
>(
	(
		{
			authenticated,
			profileGuid,
			isReady,
			isPanelOpen,
			availability,
			hasCustomisation,
			onSetPanelOpen,
			onActiveChange,
		},
		ref,
		) => {
		const [started, setStarted] = useState(false)
		const [run, setRun] = useState(false)
		const [stepIndex, setStepIndex] = useState(0)
		const [joyrideStepIndex, setJoyrideStepIndex] = useState(0)
		const [activeSteps, setActiveSteps] = useState<TutorialStep[]>([])

		const joyrideSteps = useMemo(
			() => activeSteps.map(toJoyrideStep),
			[activeSteps],
		)

		const endTutorial = useCallback(
			(seen: boolean) => {
				if (seen && profileGuid) markDrSecDimTutorialSeen(profileGuid)
				setRun(false)
				setStarted(false)
				setStepIndex(0)
				setJoyrideStepIndex(0)
				setActiveSteps([])
				onSetPanelOpen(false)
				onActiveChange?.(false)
			},
				[profileGuid, onSetPanelOpen, onActiveChange],
			)

		const beginTutorial = useCallback(
			(startIndex: number) => {
				const steps = buildActiveSteps(availability)
				if (steps.length === 0) return false

				const clampedIndex = Math.min(Math.max(startIndex, 0), steps.length - 1)
				setActiveSteps(steps)
				setStepIndex(clampedIndex)
				setJoyrideStepIndex(clampedIndex)
				setRun(true)
				setStarted(true)
				onActiveChange?.(true)
				return true
			},
			[availability, onActiveChange],
		)

		useLayoutEffect(() => {
			if (started) return
			if (!authenticated || !profileGuid) return
			if (!isReady) return
			if (hasCustomisation) return
			if (hasSeenDrSecDimTutorial(profileGuid)) return
			if (!document.querySelector('[data-tour="dr-secdim-trigger"]')) return

			beginTutorial(0)
		}, [
			started,
			authenticated,
			profileGuid,
			isReady,
			hasCustomisation,
			beginTutorial,
		])

		useImperativeHandle(
			ref,
			() => ({
				restart: () => {
					beginTutorial(0)
				},
			}),
			[beginTutorial],
		)

		const panelJustOpenedRef = useRef(false)

		const currentStep = activeSteps[stepIndex]
		const needsPanelOpen = currentStep?.panelOpen ?? false
		useLayoutEffect(() => {
			if (!started) return
			if (needsPanelOpen && !isPanelOpen) {
				panelJustOpenedRef.current = true
				onSetPanelOpen(true)
				return undefined
			}
			if (!needsPanelOpen && isPanelOpen) {
				onSetPanelOpen(false)
				setJoyrideStepIndex(stepIndex)
				return undefined
			}
			if (needsPanelOpen && isPanelOpen && panelJustOpenedRef.current) {
				panelJustOpenedRef.current = false
				return waitForPanelTransition(() => setJoyrideStepIndex(stepIndex))
			}
			setJoyrideStepIndex(stepIndex)
			return undefined
		}, [started, needsPanelOpen, isPanelOpen, stepIndex, onSetPanelOpen])

		const handleTourEvent: EventHandler = useCallback(
			data => {
				const { action, index, status, type } = data

				if (
					status === STATUS.FINISHED ||
					status === STATUS.SKIPPED ||
					type === EVENTS.TOUR_END ||
					action === ACTIONS.CLOSE
				) {
					endTutorial(true)
					return
				}

				if (type === EVENTS.STEP_AFTER) {
					const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1)
					if (nextIndex < 0) return
					if (nextIndex >= activeSteps.length) {
						endTutorial(true)
						return
					}
					setStepIndex(nextIndex)
					return
				}

				if (type === EVENTS.TARGET_NOT_FOUND) {
					const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1)
					if (nextIndex < 0 || nextIndex >= activeSteps.length) {
						endTutorial(true)
						return
					}
					setStepIndex(nextIndex)
				}
			},
			[activeSteps.length, endTutorial],
		)

		return (
			<CustomJoyride
				run={run}
				steps={joyrideSteps}
				stepIndex={joyrideStepIndex}
				callback={handleTourEvent}
			/>
		)
	},
)

DrSecDimTutorial.displayName = 'DrSecDimTutorial'

export default DrSecDimTutorial
